import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ENV } from "@app/constants/env";
import { TrfcSlotDetails, TrfcSlotDetailsUpdateDto } from '@feature/types/traffic.slot.details';
import { LinkEfairsJobListDto } from '@feature/types/link.efairsjob.list';
import { FixedQuestionOptionType } from '@feature/types/fixed.question.option.type';
import { QuestionnaireformQuestionsType } from '@feature/types/fixed.question.data.type';
import { getToken } from '@app/helpers/authProvider';
import { QuestionnaireFormQuestionType } from '@feature/types/questionnaireform.question.type';
import { TrfcSlotEntity } from '@feature/types/slot.type';
import { TrfcBookSlotDto } from '@feature/types/book.slot.dto.type';
import { TrfcSearchSlotsRequest } from '@feature/types/search.slots.param.type';

export interface GetSlotDetailsForBookingGrid extends TrfcSlotDetails {}

const API_BASE_URL = ENV.BASE_API_URL +ENV.JOB_APIURL;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      let headers: Headers;
      if ((args as FetchArgs).headers instanceof Headers) {
        headers = (args as FetchArgs).headers as Headers;
      } else {
        headers = new Headers((args as FetchArgs).headers as HeadersInit);
      }

      headers.set('Authorization', `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export const trafficJobSliceApi = createApi({
  reducerPath: 'trafficJobSliceApi',
  baseQuery: authBaseQuery,
  tagTypes: ['trafficmanagement'],
  endpoints: (builder) => ({
    getTrafficBookingsList: builder.query<ApiResponse<TrfcSlotDetails[]>, number>({
      query: (mstrShowId) => ({
        url: `Traffic/GetAllTrafficJobs?showId=${mstrShowId}`,
        method: 'GET', // FIX: Changed from POST to GET
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
    getTrafficBookingsSlotDetailsById: builder.query<ApiResponse<TrfcSlotDetails>, number>({
      query: (slotDetailId) => ({
        url: `Traffic/GetSlotDetailsById?slotDetailId=${slotDetailId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
    UpdateTrafficBookedSlot: builder.mutation<ApiResponse<TrfcSlotDetailsUpdateDto>, TrfcSlotDetailsUpdateDto>({
      query: (slotDetails) => ({
        url: 'Traffic/UpdateTrafficBookedSlot',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: slotDetails,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
    getEfairJobListForLinkingGrid: builder.query<ApiResponse<LinkEfairsJobListDto[]>, { ShowId: number; CustomerId: number }>({
      query: ({ ShowId, CustomerId }) => ({
        url: `Traffic/GetWorkOrderForLinkTrafficSlot?showId=${ShowId}&CustomerId=${CustomerId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
    GetWorkOrderForLinkFormBookings: builder.query<ApiResponse<LinkEfairsJobListDto[]>, { logId: number }>({
      query: ({ logId}) => ({
        url: `Traffic/GetWorkOrderForLinkFormBookings?logId=${logId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
    getFixQuestionOptions: builder.query<ApiResponse<FixedQuestionOptionType[]>, { ShowId: number; OptionId: number }>({
      query: ({ ShowId, OptionId }) => ({
        url: `Traffic/GetFixedQuestionOptions?showId=${ShowId}&TypeId=${OptionId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
    getQuestionnaireFormQuestions: builder.query<ApiResponse<QuestionnaireformQuestionsType[]>, { ShowId: number }>({
      query: ({ ShowId }) => ({
        url: `Traffic/GetQuestionnaireFormQuestions?showId=${ShowId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
    CreateQuestionnaireFormData: builder.mutation<ApiResponse<QuestionnaireFormQuestionType>, QuestionnaireFormQuestionType>({
      query: (question) => ({
        url: 'Traffic/CreateQuestionnaireFormQuestion',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: question,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
    UpdateQuestionnaireFormData: builder.mutation<ApiResponse<QuestionnaireFormQuestionType>, QuestionnaireFormQuestionType>({
      query: (question) => ({
        url: 'Traffic/UpdateQuestionnaireFormQuestion',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: question,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
    DeleteQuestionnaireFormQuestion: builder.mutation<ApiResponse<number>, number>({
      query: (questionnaireFormId) => ({
        url: `Traffic/DeleteQuestionnaireFormQuestion/${questionnaireFormId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
     getSlotDetailsForQuestionnaireForm: builder.query<ApiResponse<GetSlotDetailsForBookingGrid[]>, { ShowId: number }>({
      query: ({ ShowId }) => ({
        url: `Traffic/GetSlotDetailsForBookingGrid?showId=${ShowId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
     CreateSlotData: builder.mutation<ApiResponse<TrfcSlotEntity>, TrfcSlotEntity>({
      query: (slotData) => ({
        url: 'Traffic/CreateSlot',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: slotData,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
         UpdateSlotData: builder.mutation<ApiResponse<TrfcSlotEntity>, TrfcSlotEntity>({
      query: (slotData) => ({
        url: 'Traffic/UpdateSlot',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: slotData,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
   BookSlot: builder.mutation<ApiResponse<TrfcBookSlotDto>, TrfcBookSlotDto>({
             query: (slotData) => ({
        url: 'Traffic/BookSlot',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: slotData,
      }),

      invalidatesTags: ['trafficmanagement'],
    }),
    DeleteEmptySlot: builder.mutation<ApiResponse<any>, number>({
      query: (slotDetailId) => ({
        url: `Traffic/DeleteEmptySlot/${slotDetailId}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
    CancelBookingSlotDetails: builder.mutation<
      ApiResponse<any>,
      { SlotDetailId: number; ShowId: number; UserId: number }
    >({
      query: (request) => ({
        url: 'Traffic/CancelBookingSlotDetails',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: request,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
    getQuestionnaireFormAnswerDetailsForUpdate: builder.query<ApiResponse<GetSlotDetailsForBookingGrid[]>, { SlotId: number }>({
      query: ({ SlotId }) => ({
        url: `Traffic/GetSlotQuestionnaireDetailsForUpdate?SlotId=${SlotId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: ['trafficmanagement'],
    }),
     SearchSlot: builder.mutation<ApiResponse<GetSlotDetailsForBookingGrid>, TrfcSearchSlotsRequest>({
      query: (searchParam) => ({
        url: 'Traffic/SearchSlots',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: searchParam,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
    CreateFixedQuestionOption: builder.mutation<ApiResponse<FixedQuestionOptionType>, FixedQuestionOptionType>({
      query: (option) => ({
        url: 'Traffic/CreateFixedQuestionOption',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: option,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
    UpdateFixedQuestionOption: builder.mutation<ApiResponse<FixedQuestionOptionType>, FixedQuestionOptionType>({
      query: (option) => ({
        url: 'Traffic/UpdateFixedQuestionOption',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: option,
      }),
      invalidatesTags: ['trafficmanagement'],
    }),
   

deleteFixQuestionOption: builder.mutation<number, number>({
  query: (zoneId) => ({
    url: `Traffic/DeleteZonePhase/${zoneId}`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  invalidatesTags: ['trafficmanagement'],
}),
UpdateQuestionnaireFormInfo: builder.mutation<ApiResponse<any>, { ShowId: number; Userid: number; formDecryption: string }>({
    query: (data) => ({
      url: 'Traffic/UpdateQuestionnaireFormInfo',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data,
    }),
    invalidatesTags: ['trafficmanagement'],
  }),
UpdateQuestionnaireFormFloorPlan: builder.mutation<ApiResponse<any>, { ShowId: number; Userid: number; formDecryption: string ,pktfcquestionnaireformid: number,option:string }>({
    query: (data) => ({
      url: 'Traffic/UpdateQuestionnaireFormFloorPlan',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data,
    }),
    invalidatesTags: ['trafficmanagement'],
  }),
  SendSlotBookingPdf: builder.mutation<ApiResponse<any>, { slotDetailId: number; userId: number; pdfBase64: string; contactEmail?: string; shareBookingEmail?: string }>({
    query: ({ slotDetailId, userId, pdfBase64, contactEmail, shareBookingEmail }) => ({
      url: 'Traffic/GetTrfcSlotDetailsPdfData',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        SlotDetailId: slotDetailId,
        UserId: userId,
        PdfBase64: pdfBase64,
        ContactEmail: contactEmail || '',
        ShareBookingEmail: shareBookingEmail || '',
      },
    }),
    invalidatesTags: ['trafficmanagement'],
  }),
  }),
  
  
}); 

export const {
  useGetTrafficBookingsListQuery,
  useGetTrafficBookingsSlotDetailsByIdQuery,
  useUpdateTrafficBookedSlotMutation,
  useGetEfairJobListForLinkingGridQuery,
  useGetFixQuestionOptionsQuery,
  useGetQuestionnaireFormQuestionsQuery,
  useCreateQuestionnaireFormDataMutation,
  useUpdateQuestionnaireFormDataMutation,
  useDeleteQuestionnaireFormQuestionMutation,
  useGetSlotDetailsForQuestionnaireFormQuery,
  useCreateSlotDataMutation,
  useUpdateSlotDataMutation,
  useBookSlotMutation,
  useDeleteEmptySlotMutation,
  useCancelBookingSlotDetailsMutation,
  useGetQuestionnaireFormAnswerDetailsForUpdateQuery,
  useSearchSlotMutation,
  useCreateFixedQuestionOptionMutation,
  useUpdateFixedQuestionOptionMutation,
  useDeleteFixQuestionOptionMutation,
  useUpdateQuestionnaireFormInfoMutation,
  useUpdateQuestionnaireFormFloorPlanMutation,

  useGetWorkOrderForLinkFormBookingsQuery,

  useSendSlotBookingPdfMutation,

} = trafficJobSliceApi;
